import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbList,
	BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Container from "@/components/ui/container";
import cacheKeys from "@/config/cache-keys";
import pages from "@/config/pages";
import AppService from "@/services/AppService";
import { useQuery } from "@tanstack/react-query";
import {
	AlertCircle,
	CircleUserIcon,
	Loader2,
	MailIcon,
	PenLineIcon,
	Smartphone,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import {
	CopyToClipboardButton,
	UploadDocumentsButton,
	UploadLinksButton,
	GroupsTable,
	DocumentsTable,
	LinksTable,
} from "./components";

const appService = new AppService();

const SingleAccountPage = () => {
	const { t } = useTranslation();
	const { accountId, mainAccId } = useParams();
	const navigate = useNavigate();

	const accountQuery = useQuery({
		queryKey: cacheKeys.SINGLE_ACCOUNT(accountId!),
		queryFn: () => appService.getSingleAccountFullTree(accountId!),
		enabled: !!accountId,
	});

	if (accountQuery.isLoading) {
		return (
			<Container className="flex justify-center items-center py-20">
				<Loader2 className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-400" />
				<span className="ml-2 text-gray-600 dark:text-gray-300">Laster...</span>
			</Container>
		);
	}

	if (accountQuery.isError) {
		return (
			<Container className="py-10">
				<div className="border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/50 p-6 rounded-lg shadow-sm">
					<div className="flex items-center gap-3">
						<AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
						<p className="text-red-600 dark:text-red-400 font-medium">
							Feil ved henting av data
						</p>
					</div>
					<Button
						variant="outline"
						size="sm"
						className="mt-4 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
						onClick={() => accountQuery.refetch()}
					>
						Prøv igjen
					</Button>
				</div>
			</Container>
		);
	}

	if (!accountQuery.data || !accountQuery.data.Result) {
		return (
			<Container className="py-10 text-gray-600 dark:text-gray-300">
				Account med ID {accountId} ble ikke funnet.
			</Container>
		);
	}

	const account = accountQuery.data.Result.account;
	const groups = accountQuery.data.Result.groups;
	const documents = accountQuery.data.Result.documents;
	const links = accountQuery.data.Result.links;

	const contactInfo = [
		{
			icon: <MailIcon className="h-4 w-4" />,
			value: account.email,
			label: "Email",
		},
		{
			icon: <Smartphone className="h-4 w-4" />,
			value: account.phone_nr,
			label: "Telefonnummer",
		},
		{
			icon: <CircleUserIcon className="h-4 w-4" />,
			value: account.contact_name,
			label: "Kontaktperson",
		},
	];

	// main render
	return (
		<div className="bg-soft-white dark:bg-[#121212] min-h-screen">
			<Container className="mx-auto">
				{/* Breadcrumb */}
				<Breadcrumb>
					<BreadcrumbList>
						<BreadcrumbItem>
							<Link
								to={pages.Admin}
								className="text-howden-grey hover:text-gold dark:hover:text-gold-95"
							>
								{t("admin.home")}
							</Link>
						</BreadcrumbItem>
						<BreadcrumbSeparator />
						<BreadcrumbItem>
							<Link
								to={pages.AdminMainAccountDetails(mainAccId)}
								className="text-howden-grey hover:text-gold dark:hover:text-gold-95"
							>
								{account.main_account_display_name}
							</Link>
						</BreadcrumbItem>
						<BreadcrumbSeparator />
						<BreadcrumbItem>
							<Link to={pages.AdminMainAccountDetails(accountId)}>
								{account.display_name}
							</Link>
						</BreadcrumbItem>
					</BreadcrumbList>
				</Breadcrumb>

				{/* Company Details Section */}
				<Card className="mb-6 shadow-sm border">
					<CardContent className="p-6 space-y-6">
						<div className="space-y-2">
							<p className="text-sm font-medium text-howden-grey">Account</p>
							<div className="flex flex-wrap gap-4 items-end">
								<h1 className="text-3xl font-bold font-header">
									{account.display_name || "Finner ikke navn"}
								</h1>
								<p className="text-base font-medium text-howden-grey">
									{account.org_number || "Finner ikke orgnr"}
								</p>
							</div>
						</div>
						<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
							<div className="flex flex-wrap gap-3">
								{contactInfo.map(({ icon, value, label }) => (
									<CopyToClipboardButton
										key={label}
										icon={icon}
										value={value}
										label={label}
									/>
								))}
							</div>
							<div className="flex flex-wrap gap-3">
								<Button
									variant="outline"
									size="sm"
									className="px-3 py-1.5 text-sm border-moss-green-40 dark:border-pistachio-50 text-moss-green-95 dark:text-pistachio-95 hover:bg-pistachio-50 dark:hover:bg-pistachio-40"
									onClick={() =>
										navigate(
											pages.AdminMainAccountUpdate(account.main_account_id)
										)
									}
								>
									<PenLineIcon className="h-4 w-4 mr-1" />
									{t("admin.button.edit")}
								</Button>
								<UploadDocumentsButton groups={groups} />
								<UploadLinksButton groups={groups} />
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Groups Section */}
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
					<Card className="shadow-sm border rounded-lg">
						<CardHeader className="border-b py-3 pl-4">
							<CardTitle className="text-xl font-semibold font-header">
								{t("admin.table.groups")}
							</CardTitle>
						</CardHeader>
						<CardContent className="p-0">
							<div className="max-h-[400px] overflow-y-auto">
								<GroupsTable groups={groups} />
							</div>
						</CardContent>
					</Card>
				</div>
				{/* Documents and Links Section */}
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
					{/* Documents Section */}
					<Card className="shadow-sm border rounded-lg">
						<CardHeader className="border-b py-3 pl-4">
							<CardTitle className="text-xl font-semibold font-header">
								{t("admin.table.documents")}
							</CardTitle>
						</CardHeader>
						<CardContent className="p-0">
							<div className="max-h-[70vh] overflow-y-auto md:max-h-none md:overflow-visible">
								<DocumentsTable documents={documents} groups={groups} />
							</div>
						</CardContent>
					</Card>

					{/* Links Section */}
					<Card className="shadow-sm border rounded-lg">
						<CardHeader className="border-b py-3 pl-4">
							<CardTitle className="text-xl font-semibold font-header">
								{t("admin.table.links")}
							</CardTitle>
						</CardHeader>
						<CardContent className="p-0">
							<div className="max-h-[70vh] overflow-y-auto md:max-h-none md:overflow-visible">
								<LinksTable links={links} groups={groups} />
							</div>
						</CardContent>
					</Card>
				</div>
			</Container>
		</div>
	);
};

export default SingleAccountPage;
